import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { X, AlertTriangle, Trash2 } from 'lucide-react';
import { Button } from './ui/button';

interface DeleteYapModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (reason: string) => void;
    isDeleting?: boolean;
    isSelfDelete?: boolean;
}

export function DeleteYapModal({ isOpen, onClose, onConfirm, isDeleting = false, isSelfDelete = false }: DeleteYapModalProps) {
    const [reason, setReason] = useState('');
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);
        return () => setMounted(false);
    }, []);

    if (!mounted) return null;

    return createPortal(
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-100 flex items-center justify-center px-4 bg-black/60 backdrop-blur-sm">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="w-full max-w-md bg-[#FAF8F5] dark:bg-[#1a1a1a] rounded-2xl shadow-xl overflow-hidden border border-black/10 dark:border-white/10"
                >
                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-black/5 dark:border-white/5">
                        <h3 className="text-xl font-bold text-[#1a1a1a] dark:text-white flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-[#E74C3C]" />
                            {isSelfDelete ? 'Delete Yap' : 'Delete Yap (Moderation)'}
                        </h3>
                        <button
                            onClick={onClose}
                            className="p-2 -mr-2 rounded-full hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                        >
                            <X className="w-5 h-5 text-[#666666] dark:text-[#a0a0a0]" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                        <p className="text-[#666666] dark:text-[#a0a0a0] mb-4 text-sm">
                            {isSelfDelete 
                                ? "Are you sure you want to delete this Yap? This action cannot be undone."
                                : "You are about to delete this Yap. Please provide a reason for the user. They will be notified that a watcher/moderator removed their post."
                            }
                        </p>

                        {!isSelfDelete && (
                            <div className="space-y-3">
                                <label className="block text-sm font-medium text-[#1a1a1a] dark:text-white">
                                    Reason for deletion
                                </label>
                                <textarea
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    placeholder="e.g., Violation of community guidelines, Hate speech, Spam..."
                                    className="w-full h-32 p-3 rounded-xl bg-white dark:bg-[#2a2a2a] border border-black/10 dark:border-white/10 text-[#1a1a1a] dark:text-white placeholder:text-[#999999] focus:outline-none focus:ring-2 focus:ring-[#E74C3C] resize-none"
                                />
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="flex items-center gap-3 px-6 py-4 bg-black/5 dark:bg-white/5">
                        <Button
                            variant="ghost"
                            onClick={onClose}
                            className="flex-1 h-12 rounded-xl text-[#666666] dark:text-[#a0a0a0] hover:bg-black/5 dark:hover:bg-white/5"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={() => onConfirm(reason)}
                            disabled={(!isSelfDelete && !reason.trim()) || isDeleting}
                            className="flex-1 h-12 rounded-xl bg-[#E74C3C] hover:bg-[#c0392b] text-white disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isDeleting ? 'Deleting...' : 'Delete Yap'}
                        </Button>
                    </div>
                </motion.div>
            </div>
            )}
        </AnimatePresence>,
        document.body
    );
}
